import React, { useMemo, useState } from "react";
import { LegendKey } from "../../models/chart_model";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { useDispatch } from "react-redux";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "../../pages/Dashboard/Exchange3/Chart";

// Interface for the calculated statistics, including extra info for min and max.
export interface LegendStats {
  name: string;
  min: number;
  max: number;
  avg: number;
  total: number;
  minExtra: Record<string, any>;
  maxExtra: Record<string, any>;
  // Optional price fields:
  minPrice?: number;
  maxPrice?: number;
  wtdAvg?: number;
}

export interface LegendSummaryTableProps {
  legends: LegendKey[];
  data: any[];
  block_key?: string;
  tableData?: any[]; // Optional: data to be displayed in the table (not used here)
  unit?: "mw" | "mwh" | "mus" | string;
  extraKeys: string[]; // e.g. ["time_block", "date"]
  extraLabels: string[]; // e.g. ["Time Block", "Date"]
  itemsPerPage?: number; // Optional: number of items per page (not used here)
  priceLegends?: LegendKey[]; // Optional: price legends matching legends by index
  filename?: string; // Optional: filename for CSV download
  showTotal?: boolean; // Optional: whether to show the total column
  showWtdAvg?: boolean; // Optional: whether to show weighted average
  showTotalEmpty?: boolean; // Optional: whether to show total as empty when no data
  defaultSortKey?: string; // Optional: default sort key for ChartTable
  isSmall?: boolean; // Optional: whether to use smaller styling for compact layouts
}

/**
 * Calculate summary statistics for each legend along with extra information from the data record
 * where the min and max values occurred.
 */
export function calculateLegendStats(
  unit: string,
  legends: LegendKey[],
  data: any[],
  extraKeys: string[] = [],
  tableData: any[] = [],
  showWtdAvg: boolean = false
): LegendStats[] {
  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key)
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    return `${legend.name} (${suffix})`;
  }

  return legends.map((legend) => {
    const { dataKey } = legend;
    let minVal = Infinity;
    let maxVal = -Infinity;
    let total = 0;
    let avgTotal = 0;
    let count = 0;
    let minRecord: any = null;
    let maxRecord: any = null;

    // Weighted average calculations
    let wtdAvgTotal = 0;
    let wtdAvgCount = 0;

    data.forEach((item) => {
      const value = Number(item[dataKey]);

      if (isNaN(value)) return;

      if (value < minVal) {
        minVal = value;
        minRecord = item;
      }
      if (value > maxVal) {
        maxVal = value;
        maxRecord = item;
      }
      avgTotal += value;
      count++;

      // Calculate weighted average if showWtdAvg is true
      // Calculate weighted average if showWtdAvg is true
      if (showWtdAvg) {
        // Only calculate weighted average for Actual data
        if (dataKey === "Actual_" || dataKey.includes("Actual")) {
          // For trend tab data, construct the volume and price keys dynamically
          let volumeKey = "Actual_mcv_mw";
          let priceKey = "Actual_wt_mcp_rs_kwh";

          // If dataKey contains a date pattern (like "2025-07-01 to 2025-07-01_Actual_wt_mcp_rs_kwh")
          if (typeof dataKey === "string" && dataKey.includes("_Actual_")) {
            const datePrefix = dataKey.split("_Actual_")[0];
            volumeKey = `${datePrefix}_Actual_mcv_mw`;
            priceKey = `${datePrefix}_Actual_wt_mcp_rs_kwh`;
          }

          const volume = Number(item[volumeKey]) || 0;
          const weightedPrice = Number(item[priceKey]) || 0;

          if (volume > 0 && weightedPrice > 0) {
            wtdAvgTotal += volume * weightedPrice; // sum of (mcv * wt_mcp)
            wtdAvgCount += volume; // sum of mcv (total volume)
          }
        }
        // For Forecast data, we don't calculate weighted average - it will remain 0
      }
    });

    let divideBy = 4;

    tableData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (isNaN(value)) return;
      total += value;
    });

    if (typeof dataKey === "string" && dataKey.endsWith("_mw")) {
      total = total / divideBy;
    }

    const avg = count > 0 ? avgTotal / count : 0;
    const wtdAvg =
      showWtdAvg && wtdAvgCount > 0 ? wtdAvgTotal / wtdAvgCount : 0;

    const minExtra: Record<string, any> = {};
    const maxExtra: Record<string, any> = {};
    extraKeys.forEach((key) => {
      minExtra[key] = minRecord ? minRecord[key] : "-";
      maxExtra[key] = maxRecord ? maxRecord[key] : "-";
    });

    return {
      name: calculateLegends(legend),
      min: count > 0 ? minVal : 0,
      max: count > 0 ? maxVal : 0,
      avg,
      total,
      minExtra,
      maxExtra,
      unit: legend.unit,
      wtdAvg: showWtdAvg
        ? dataKey === "Actual_" || dataKey.includes("Actual")
          ? wtdAvg
          : undefined
        : undefined,
    };
  });
}

/**
 * A component that displays a summary table of legend statistics.
 * In addition to the regular columns (Min, Max, Avg, Total), if priceLegends are provided,
 * the Min and Max groups will each display two main columns: one for the "Mu" (value)
 * and one for "Price", followed by extra sub-columns.
 *
 * For example, if extraKeys is ["time_block","date"], the sub-headers for the Min group will be:
 *   Mu | Price | Time Block | Date
 *
 * The component computes the price values by scanning the data for the corresponding price legend key
 * (matched by the same index in the priceLegends array).
 */
const LegendSummaryTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit = "MWh",
  extraKeys,
  tableData = [],
  extraLabels,
  block_key,
  priceLegends,
  showTotal = true,
  showWtdAvg = false,
  showTotalEmpty = false,
}) => {
  console.log("Summary Table data", data)
  // Pair each extra key with its label.
  const extras = extraKeys.map((key, idx) => ({
    key,
    label: extraLabels[idx] || key,
  }));
  const dispatch = useDispatch();

  // Calculate basic stats for the legends.
  const stats = calculateLegendStats(
    unit,
    legends,
    data,
    extraKeys,
    tableData,
    showWtdAvg
  );

  // If priceLegends are provided, compute minPrice and maxPrice for each legend.
  const statsWithPrice = stats.map((stat, index) => {
    if (priceLegends && priceLegends[index]) {
      const priceKey = priceLegends[index].dataKey;
      let minPrice = Infinity;
      let maxPrice = -Infinity;
      data.forEach((item) => {
        const price = Number(item[priceKey]);
        if (!isNaN(price)) {
          if (price < minPrice) minPrice = price;
          if (price > maxPrice) maxPrice = price;
        }
      });

      return {
        ...stat,
        minPrice: minPrice === Infinity ? 0 : minPrice,
        maxPrice: maxPrice === -Infinity ? 0 : maxPrice,
      };
    }

    dispatch(setLoadingFilterName(""));
    return stat;
  });

  // Adjust total if unit is 'mw': convert total to MWh by dividing by 4.

  const adjustedStats = statsWithPrice.map((stat) => ({
    ...stat,
  }));

  // Determine the label for numeric values.
  const valueUnitLabel = unit === "MW" ? "MW" : unit === "MWh" ? "MWh" : unit;

  // Determine how many sub-columns each group should have.
  // If priceLegends exist, then for each group (Min/Max) we have: "Mu", "Price", plus the extra columns.
  const groupColumnCount = (priceLegends ? 2 : 1) + extras.length;

  return (
    <div className="overflow-x-auto capitalize">
      <table className="min-w-full text-nowrap ">
        <thead className="sticky top-0 z-10 ">
          {/* First header row: group labels */}
          <tr className="relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-[#fdd3bb]">
            <th
              rowSpan={2}
              className="py-2 px-6 text-center z-10 relative after:content-[''] border-0 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Name
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Minimum
            </th>
            <th
              colSpan={groupColumnCount}
              className="py-2 px-6 text-center text-[#34656D] border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
            >
              Maximum
            </th>
            <th
              rowSpan={2}
              className={`py-2 px-6 text-center z-10 relative border-0 ${showTotal === false && showWtdAvg === false
                  ? ""
                  : "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
                }`}
            >
              Average
            </th>
            {showTotal && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Total
              </th>
            )}
            {showWtdAvg && (
              <th rowSpan={2} className="py-2 px-6 z-10 border-0 text-center">
                Wt. Avg
              </th>
            )}
          </tr>
          {/* Second header row: sub-columns */}
          <tr className="text-gray-700">
            {/* Min group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`min-extra-header-${idx}`}
                className="py-2 px-6 text-center capitalize border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
            {/* Max group sub-columns */}
            <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
              Value
            </th>
            {priceLegends && (
              <th className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]">
                {priceLegends[0].unit}
              </th>
            )}
            {extras.map((extra, idx) => (
              <th
                key={`max-extra-header-${idx}`}
                className="py-2 px-6 text-center border-0 relative after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#fdd3bb]"
              >
                {extra.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-[13px]">
          {adjustedStats.map((stat, index) => (
            <tr
              key={index}
              className="border-b rounded-xl border-gray-200 hover:bg-gray-100 text-center"
            >
              <td className="py-3 px-6 text-center ">
                {(() => {
                  // Parse the name to find unit in parentheses
                  const match = stat.name.match(/(.*)\s*\((.*?)\)$/);
                  if (match) {
                    const [_, nameText, unitText] = match;
                    return (
                      <>
                        <span>{nameText}</span>{" "}
                        <span className="text-[#34656D] font-medium">
                          ({unitText})
                        </span>
                      </>
                    );
                  } else {
                    // If no unit pattern found, just return the whole name
                    return stat.name;
                  }
                })()}
              </td>
              {/* Min group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.min.toFixed(2)}
              </td>
              {/* Min group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6 ">
                  {stat.minPrice !== undefined ? stat.minPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Min group: Extra columns */}
              {extras.map((extra, idx) => {
                return (
                  <td
                    key={`min-extra-${index}-${idx}`}
                    className={`py-3 px-6 ${extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                      }`}
                  >
                    {stat.minExtra[extra.key] !== undefined
                      ? (extra.key == block_key ? (stat.minExtra[extra.key] + " (" + getTimeRange(stat.minExtra[extra.key]) + ")") : stat.minExtra[extra.key])
                      : "-"}
                  </td>
                );
              })}
              {/* Max group: Mu column */}
              <td className="py-3 px-6 text-[#34656D] font-medium">
                {stat.max.toFixed(2)}
              </td>
              {/* Max group: Price column */}
              {priceLegends && (
                <td className="py-3 px-6">
                  {stat.maxPrice !== undefined ? stat.maxPrice.toFixed(2) : "-"}
                </td>
              )}
              {/* Max group: Extra columns */}
              {extras.map((extra, idx) => (
                <td
                  key={`min-extra-${index}-${idx}`}
                  className={`py-3 px-6 ${extra.key === "block" ? "text-[#F1935C] font-medium" : ""
                    }`}
                >
                  {stat.maxExtra[extra.key] !== undefined
                    ? (extra.key == block_key ? (stat.maxExtra[extra.key] + " (" + getTimeRange(stat.maxExtra[extra.key]) + ")") : stat.maxExtra[extra.key])
                    : "-"}
                </td>
              ))}
              <td className="py-3 px-6">
                <span className=" font-medium">{stat.avg.toFixed(2)}</span>
              </td>
              {showTotal && (
                <td className="py-3 px-6">
                  {(() => {
                    const match = stat.name.match(/.*\((.*?)\)$/);
                    let unit = match ? match[1] : "";

                    // If it's a percentage, rate (contains Rs), or the total is NaN, show "-"
                    if (
                      isNaN(stat.total) ||
                      unit === "%" ||
                      unit.includes("Rs") ||
                      unit.includes("₹") ||
                      stat.name.includes("%") ||
                      stat.name.toLowerCase().includes("percent")
                    ) {
                      return <span>-</span>;
                    }

                    if (unit.toLowerCase() === "mw") {
                      unit = "MWh";
                    }
                    return (
                      <span className="font-medium">
                        {showTotalEmpty ? (
                          <span>-</span>
                        ) : (
                          <>
                            {" "}
                            {stat.total.toFixed(2)} {" ("}
                            {unit}
                            {")"}
                          </>
                        )}
                      </span>
                    );
                  })()}
                </td>
              )}
              {showWtdAvg && (
                <td className="py-3 px-6">
                  <span className="font-medium">
                    {stat.wtdAvg === null
                      ? "-"
                      : stat.wtdAvg !== undefined
                        ? stat.wtdAvg.toFixed(2)
                        : "-"}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {priceLegends && (
        <div className="text-center text-sm text-gray-500 mt-4 ">
          The Min and Max {priceLegends[0].unit} is corresponding to the Min and
          Max {extraKeys.join(", ")} of {valueUnitLabel}
        </div>
      )}
    </div>
  );
};

export default LegendSummaryTable;

/**
 * Downloads the summary data in CSV format.
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh"). If "mw", total is converted to MWh.
 * @param extraKeys - Array of keys to extract extra info from the data records.
 * @param extraLabels - Array of labels corresponding to the extra keys.
 */
export function downloadSummaryCSV({
  legends,
  data,
  unit = "MWh",
  tableData,
  extraKeys,
  extraLabels,
  filename = "summary.csv",
}: LegendSummaryTableProps): void {
  // Calculate summary statistics using the same logic as in LegendSummaryTable.
  const stats = calculateLegendStats(unit, legends, data, extraKeys, tableData);

  // If unit is "mw", convert the total to MWh (divide by 4); otherwise, leave as is.
  const adjustedStats = stats.map((stat) => ({
    ...stat,
  }));

  // Determine the label for the numeric values.
  const valueUnitLabel = unit === "mw" ? "MW" : unit === "mwh" ? "MWh" : "MUs";

  // Construct the CSV header.
  // Columns: Name, Min (unit), [for each extra label: Min - {label}], Max (unit), [for each extra label: Max - {label}], Avg (unit), Total (MWh)
  const headers = [
    "Name",
    `Min (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Min - ${label}`),
    `Max (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Max - ${label}`),
    `Avg (${valueUnitLabel})`,
    `Total (${unit === "mw" ? "MWh" : unit === "mwh" ? "MWh" : "MUs"})`,
  ];

  // Build CSV rows for each legend summary.
  const rows = adjustedStats.map((stat) => {
    const row = [];
    row.push(stat.name);
    row.push(stat.min.toFixed(2));
    // Append each extra value for the min record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.minExtra[extraKeys[i]]);
    }
    row.push(stat.max.toFixed(2));
    // Append each extra value for the max record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.maxExtra[extraKeys[i]]);
    }
    row.push(stat.avg.toFixed(2));
    row.push(stat.total.toFixed(2));
    return row;
  });

  // Combine headers and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob from the CSV string and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Summary Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

type SortConfig = {
  // When composite is true, data is sorted by extraKeys[0] (and extraKeys[1] if available)
  composite: boolean;
  key: string | null;
  direction: "asc" | "desc";
};

export const ChartTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
  block_key,
  itemsPerPage: itemsPerPageProp,
  defaultSortKey: defaultSortKeyProp,
  isSmall = false,
}) => {
  const itemsPerPage = itemsPerPageProp || 50;
  const dispatch = useDispatch();
  const defaultSortKey =
    defaultSortKeyProp || (extraKeys.length > 0 ? extraKeys[extraKeys.length - 1] : "");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    composite: false,
    key: defaultSortKey,
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleSort = (columnKey: string) => {
    if (sortConfig.composite || sortConfig.key !== columnKey) {
      setSortConfig({ composite: false, key: columnKey, direction: "desc" });
    } else {
      setSortConfig({
        composite: false,
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    }
    setCurrentPage(1);
  };

  const compareValues = (aValue: any, bValue: any): number => {
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;
    if (typeof aValue === "number" && typeof bValue === "number") {
      return aValue - bValue;
    }
    return aValue.toString().localeCompare(bValue.toString());
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.composite) {
        let cmp = compareValues(a[extraKeys[0]], b[extraKeys[0]]);
        if (cmp === 0 && extraKeys.length > 1) {
          cmp = compareValues(a[extraKeys[1]], b[extraKeys[1]]);
        }
        return -cmp;
      } else if (sortConfig.key) {
        let cmp = compareValues(a[sortConfig.key], b[sortConfig.key]);
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig, extraKeys]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const renderSortArrow = (columnKey: string) => {
    if (!sortConfig.composite && sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key)
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    return `${legend.name} (${suffix})`;
  }

  const headerColumns = [
    ...extraKeys.map((key, index) => ({
      key,
      label: extraLabels[index] || key,
    })),
    ...legends.map((legend) => ({
      key: legend.dataKey,
      // label: `${legend.name} (${unit?.toUpperCase() ?? legend.unit})`,
      label: calculateLegends(legend),
    })),
  ];

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  dispatch(setLoadingFilterName(""));
  return (
    <div className={`chartTable ${isSmall ? 'isSmall' : ''}`}>
      {/* In small mode, avoid inner vertical scrolling to prevent double scrollbars */}
      <div
        className={`${isSmall ? "overflow-x-auto overflow-y-visible" : "overflow-auto text-nowrap"} capitalize`}
        style={isSmall ? undefined : { maxHeight: "calc(100vh - 333px)" }}
      >
        <table className={`${isSmall ? "w-full" : ""}`}>
          <thead className="sticky top-0 z-10">
            <tr className={isSmall ? "text-[10px]" : "text-sm"}>
              {headerColumns.map((col, index) => {
                return (
                  <th
                    key={index}
                    style={{ fontSize: isSmall ? "10px" : undefined }}
                    className={`${isSmall ? "py-1 px-2  " : "py-3 px-4"} cursor-pointer text-center`}
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="flex items-center justify-center">
                      {(() => {
                        // Parse the label to find unit in parentheses
                        const match = col.label.match(
                          /(.*)\s*\((MW|MWH|MUS|Rs\/kWh|LU|₹\/kWh|Rs|%)\)$/i
                        );

                        if (match) {
                          const [_, nameText, unitText] = match;

                          // Determine color for unit
                          let unitColorClass = "";

                          unitColorClass = "text-[#34656D]";

                          return (
                            <>
                              <span className="font-medium">{nameText} </span>
                              <span
                                className={`${unitColorClass} ms-1 font-medium`}
                              >
                                ({unitText})
                              </span>
                              <span className="font-medium"></span>
                            </>
                          );
                        } else {
                          // If no unit pattern found, just return the whole label
                          return <span>{col.label}</span>;
                        }
                      })()}
                      <span className="ml-1">{renderSortArrow(col.key)}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={`text-[#8C8A96] font-normal ${isSmall ? "text-[11px]" : "text-[13px]"}`}>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-gray-200 hover:bg-gray-100 text-center"
              >
                {extraKeys.map((key, index) => {
                  return (
                    <td
                      key={index}
                      className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} text-center ${key === "block" ? "text-[#F1935C] font-medium" : ""
                        }`}
                    >
                      {row[key] !== undefined ? key == block_key ? (row[key] + " (" + getTimeRange(row[key]) + ")") : row[key] : "-"}
                    </td>
                  );
                })}
                {legends.map((legend, index) => (
                  <td key={index} className={`${isSmall ? "py-1 px-2" : "py-3 px-4"}`}>
                    {formatCellValue(row[legend.dataKey])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls - uncomment if needed */}
      <div className={`flex justify-between items-center ${isSmall ? "mt-2" : "mt-4"}`}>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`${isSmall ? "px-2 py-1 text-xs" : "px-3 py-1"} border rounded disabled:opacity-50`}
        >
          Previous
        </button>
        <div className={isSmall ? "text-xs" : ""}>
          Page {currentPage} of {totalPages}
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`${isSmall ? "px-2 py-1 text-xs" : "px-3 py-1"} border rounded disabled:opacity-50`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const formatCellValue = (value: any): string => {
  if (value === undefined || value === null) return "-";
  const num = Number(value);
  // If value is numeric (even if originally a string), format it.
  if (!isNaN(num)) {
    return num.toFixed(2);
  }
  return value.toString();
};

/**
 * Downloads the summary data (displayed in ChartTable/LegendSummaryTable) as a CSV file.
 * The CSV columns are built as follows:
 *   - The first columns come from extraKeys (labeled using extraLabels)
 *   - The remaining columns come from legends (header includes the legend name and unit)
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh").
 * @param extraKeys - Keys to display as the first columns.
 * @param extraLabels - Corresponding labels for the extra keys.
 */
export function downloadChartTableCSV({
  filename = "chart_table.csv",
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
}: LegendSummaryTableProps): void {
  // Build the header row:
  // Use extraLabels for the extra columns, and for each legend add "legendName (UNIT)"

  function calculateLegends(legend: any) {
    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key)
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    return `${legend.name} (${suffix})`;
  }
  const headers = [
    ...extraKeys.map((key, idx) => extraLabels[idx] || key),
    ...legends.map((legend) => calculateLegends(legend)),
  ];

  // Build CSV rows from each data object.
  // For each row, first output values for extraKeys, then for each legend's dataKey.
  const rows = data.map((row) => {
    const extraValues = extraKeys.map((key) => {
      // Wrap values in quotes if they contain commas
      const value = row[key] !== undefined ? row[key] : "";
      return typeof value === "string" && value.includes(",")
        ? `"${value}"`
        : value;
    });
    const legendValues = legends.map((legend) => {
      const value =
        row[legend.dataKey] !== undefined ? row[legend.dataKey] : "";
      return typeof value === "string" && value.includes(",")
        ? `"${value}"`
        : value;
    });
    return [...extraValues, ...legendValues];
  });

  // Combine header and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Chart Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
